import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import FeedbackDrawer from '@modules/feedback/components/FeedbackCard/FeedbackDrawer';
import { FeedbackListStoreProvider } from '@modules/feedback/store/useFeedbackListStore';
import { ExpandedFeedbackContent } from '@modules/feedbackV2/expanded/components/ExpandedFeedbackContent';
import { ChatRounded } from '@mui/icons-material';
import { Box, Button, Skeleton, Stack } from '@mui/material';
import { FeedbackSummaryUserQuote } from 'generated/graphql';
import { useMemo, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetFeedback } from 'v2/core/services/records';

const trackFeedbackListSummaryEvents = createAnalyticsTracker(
  'feedbackListSummary',
);

interface UserQuoteHeaderProps {
  recordID: string;
  summaryUserQuote: FeedbackSummaryUserQuote;
}

export function UserQuoteHeader({
  recordID,
  summaryUserQuote,
}: UserQuoteHeaderProps) {
  const { feedback, status } = useGetFeedback(recordID);

  const [isFeedbackDetailsDrawerVisible, setIsFeedbackDetailsDrawerVisible] =
    useState(false);

  const sourceIcon = summaryUserQuote.sourceIcon ?? feedback?.sourceV2?.logo;
  const sourceName = summaryUserQuote.source ?? feedback?.sourceV2?.name;

  const renderSourceTitle = () => {
    if (!sourceName) {
      return <Skeleton variant="rounded" width="200px" height="12px" />;
    }

    return (
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {sourceIcon && <Box component="img" src={sourceIcon} width={18} />}
        <TypographyWithEllipsis
          variant="subtitle1"
          tooltipProps={{
            placement: 'top',
            PopperProps: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -6],
                  },
                },
              ],
            },
          }}
        >
          {sourceName}
        </TypographyWithEllipsis>
      </Stack>
    );
  };

  const { labelID, startTime, endTime } = summaryUserQuote;

  const selectedLabels = useMemo(() => {
    const label = feedback?.labels?.find((label) => label.id === labelID);
    return label ? [label] : undefined;
  }, [feedback?.labels, labelID]);

  const selectedAudioSnippetTime = useMemo(() => {
    if (startTime === undefined || endTime === undefined) {
      return;
    }
    return {
      startTime,
      endTime,
    };
  }, [startTime, endTime]);

  return (
    <>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        minWidth="max-content"
        width="100%"
      >
        {renderSourceTitle()}
        <Stack flexShrink="0">
          <Button
            size="small"
            onClick={() => {
              setIsFeedbackDetailsDrawerVisible(true);
              trackFeedbackListSummaryEvents({
                eventName:
                  'Feedback List Summary - Expand Button In User Quote Clicked',
              });
            }}
            variant="text"
            startIcon={<ChatRounded fontSize="small" />}
          >
            View feedback record
          </Button>
        </Stack>
      </Stack>
      <FeedbackDrawer
        unmountOnClose={true}
        isOpen={isFeedbackDetailsDrawerVisible}
        onClose={() => setIsFeedbackDetailsDrawerVisible(false)}
      >
        {feedback && (
          <FeedbackListStoreProvider>
            <ExpandedFeedbackContent
              feedback={feedback}
              isLoading={IsLoading(status)}
              selectedPredictionLabels={selectedLabels ?? []}
              setSelectedPredictionLabels={() => {}}
              selectedAudioSnippetTime={selectedAudioSnippetTime}
            />
          </FeedbackListStoreProvider>
        )}
      </FeedbackDrawer>
    </>
  );
}
